import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useReveal from "@/hooks/useReveal";

const ENTRIES = [
  {
    id: "001",
    issue: "Issue 001",
    title: "Vol. I · The Africa Frame™",
    excerpt:
      "Edition 001. The face-on study of the gold-tone prototype — Africa-shaped left, hexagonal right.",
    image:
      "https://customer-assets.emergentagent.com/job_afrqa-luxury/artifacts/0esx8z7g_fcda3137-3915-43e7-b3f9-6b5055ad7dbd.JPG",
    tag: "Real Prototype · Vol. I",
    bg: "bg-[#0c0c0c]",
    real: true,
    span: "lg:col-span-7 lg:row-span-2 aspect-[5/6] lg:aspect-auto",
  },
  {
    id: "002",
    issue: "Issue 002",
    title: "Rooftop · Golden Hour",
    excerpt:
      "Editorial study against city light.",
    image:
      "https://customer-assets.emergentagent.com/job_afrqa-luxury/artifacts/pqgm23us_afrqa%20model%203.PNG",
    tag: "Campaign · Vol. I",
    bg: "bg-[#0c0c0c]",
    span: "lg:col-span-5 aspect-[4/5]",
  },
  {
    id: "003",
    issue: "Issue 003",
    title: "Side Profile · Gold Hardware",
    excerpt:
      "18k gold-tone temple. Weightless on the face.",
    image:
      "https://customer-assets.emergentagent.com/job_afrqa-luxury/artifacts/31u5d5k2_3eb31fc8-5e84-49b0-81ae-8fee1cb47ade.JPG",
    tag: "Craftsmanship",
    bg: "bg-[#0c0c0c]",
    real: true,
    span: "lg:col-span-5 aspect-[5/4]",
  },
  {
    id: "004",
    issue: "Issue 004",
    title: "The Timepiece · First Look",
    excerpt:
      "Edition 002 enters the archive.",
    image:
      "https://customer-assets.emergentagent.com/job_afrqa-luxury/artifacts/yrumj0o4_Afrqa%20watch.PNG",
    tag: "Preview · Phase II",
    bg: "bg-[#e6d5b8]",
    real: true,
    span: "lg:col-span-5 aspect-[4/5]",
  },
  {
    id: "005",
    issue: "Issue 005",
    title: "Portrait · The Africa Frame™",
    excerpt:
      "Worn. The frame becomes a face.",
    image:
      "https://customer-assets.emergentagent.com/job_afrqa-luxury/artifacts/0d8djkx6_afrqa%20model.PNG",
    tag: "Editorial",
    bg: "bg-[#0c0c0c]",
    span: "lg:col-span-7 aspect-[5/4]",
  },
  {
    id: "006",
    issue: "Issue 006",
    title: "The Unboxing",
    excerpt:
      "Black, gold, marble. The delivery ritual.",
    image:
      "https://customer-assets.emergentagent.com/job_afrqa-luxury/artifacts/5fxb2oas_afrqa%20case.PNG",
    tag: "Object Study",
    bg: "bg-[#0c0c0c]",
    real: true,
    span: "lg:col-span-12 aspect-[16/9]",
  },
];

function JournalCard({ entry }) {
  const r = useReveal();
  return (
    <article
      ref={r.ref}
      className={`reveal ${r.inView ? "in-view" : ""} relative group ${entry.span}`}
      data-testid={`journal-entry-${entry.id}`}
    >
      <div
        className={`zoom-wrap ${entry.real ? "prototype-photo" : ""} relative w-full h-full overflow-hidden border border-[#1f1f1f] ${entry.bg}`}
      >
        <img
          src={entry.image}
          alt={entry.title}
          className="w-full h-full object-cover"
        />
        {entry.real && (
          <span
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.40) 100%)",
            }}
          />
        )}
        <span
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.15) 40%, transparent 70%)",
          }}
        />

        {/* Top corner: issue */}
        <div className="absolute top-5 left-5 right-5 flex items-center justify-between font-label text-[#cfcfca]">
          <span>{entry.issue}</span>
          <span className="text-[#d4af37]">{entry.tag}</span>
        </div>

        {/* Bottom: title + excerpt */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <h3 className="font-display text-white text-[clamp(1.5rem,2.6vw,2.4rem)] leading-tight">
            {entry.title}
          </h3>
          <p className="mt-3 text-[#cfcfca] text-sm md:text-base font-light max-w-md leading-relaxed">
            {entry.excerpt}
          </p>
          <span className="mt-5 inline-flex items-center gap-3 font-label text-[#d4af37] group-hover:text-white transition-colors">
            <span className="w-6 h-px bg-current" />
            Read the Story
          </span>
        </div>
      </div>
    </article>
  );
}

export default function JournalPage() {
  const r1 = useReveal();
  const r2 = useReveal();

  return (
    <div data-testid="journal-page" className="bg-[#0a0a0a] text-white">
      <Header />
      <main className="pt-28 md:pt-36 pb-24">
        <section className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div
            ref={r1.ref}
            className={`reveal ${r1.inView ? "in-view" : ""} max-w-3xl`}
          >
            <span className="font-label text-[#d4af37]">— The Archive —</span>
            <h1 className="mt-5 font-display text-[clamp(2.6rem,6vw,5.4rem)] leading-[0.96] tracking-[-0.025em]">
              Journal &{" "}
              <span className="font-editorial italic gold-text-gradient">
                editorials.
              </span>
            </h1>
            <p className="mt-8 max-w-xl text-[#cfcfca] text-lg leading-relaxed font-light">
              Visual storytelling from the AFRQA™ universe — campaigns,
              craftsmanship, and the moods that shape future culture.
            </p>
          </div>
        </section>

        <section className="mt-16 md:mt-20 max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6 auto-rows-[minmax(420px,auto)] lg:auto-rows-[minmax(360px,auto)]">
            {ENTRIES.map((e) => (
              <JournalCard key={e.id} entry={e} />
            ))}
          </div>
        </section>

        {/* Phase II close */}
        <section
          ref={r2.ref}
          className={`reveal ${r2.inView ? "in-view" : ""} mt-28 md:mt-36 max-w-[1400px] mx-auto px-6 md:px-10`}
        >
          <div className="border border-[#1f1f1f] bg-[#070707] p-10 md:p-16 text-center relative overflow-hidden">
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at top, rgba(212,175,55,0.08) 0%, transparent 65%)",
              }}
            />
            <div className="relative">
              <span className="font-label text-[#d4af37]">— Next Issue —</span>
              <h2 className="mt-5 font-editorial italic gold-text-gradient text-[clamp(2rem,5vw,4rem)] leading-[1.05]">
                Adaptive Vision.
              </h2>
              <p className="mt-5 max-w-xl mx-auto text-[#a3a3a3] font-light">
                Phase II of the archive — loading.
              </p>
              <Link
                to="/vision"
                data-testid="journal-phase-2-cta"
                className="btn-ghost-gold mt-10 inline-flex"
              >
                Read the Manifesto
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
